// Game-board renderer (Canvas2D). Owns the board canvas; the post-FX layer composites it.

import {
  BOARD_BUFFER_HEIGHT,
  BOARD_VISIBLE_HEIGHT,
  BOARD_WIDTH,
} from '../config';
import type { Engine } from '../game/engine';
import { shapeOf } from '../game/pieces';
import type { Cell, PieceType, Rotation } from '../types';
import type { Accolades } from './accolades';
import type { Particles } from './particles';
import type { ThemePalette } from './palettes';

export interface RenderOptions {
  cellPx: number;
  showGhost: boolean;
  showGrid: boolean;
  rgbCellStyle: 'flat' | 'jewel' | 'beveled' | 'outline' | 'glass' | 'neon' | 'pixel';
  ghostAlpha: number;
}

export const DEFAULT_RENDER_OPTIONS: RenderOptions = {
  cellPx: 32,
  showGhost: true,
  showGrid: true,
  rgbCellStyle: 'jewel',
  ghostAlpha: 0.18,
};

interface ShakeState { x: number; y: number; strength: number; ttlMs: number; }

export class BoardRenderer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  palette: ThemePalette;
  options: RenderOptions;
  shake: ShakeState = { x: 0, y: 0, strength: 0, ttlMs: 0 };
  // Animation state for line clear flash.
  flashTtlMs = 0;
  flashRows: number[] = [];
  // Top-out animation
  topoutTtlMs = 0;
  topoutDur = 900;
  // Brief in-game controls hint, faded in at game start.
  controlsHintTtlMs = 0;
  controlsHintLife = 4500;

  constructor(canvas: HTMLCanvasElement, palette: ThemePalette, options: RenderOptions) {
    this.canvas = canvas;
    // desynchronized=true asks the browser to skip waiting on the compositor,
    // which can meaningfully reduce frame time when uploading the canvas as
    // a WebGL texture every frame.
    const ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) throw new Error('2D canvas not available');
    this.ctx = ctx;
    this.palette = palette;
    this.options = options;
  }

  setPalette(p: ThemePalette): void { this.palette = p; }
  setOptions(o: Partial<RenderOptions>): void { this.options = { ...this.options, ...o }; }

  shakeBoard(strength: number, durationMs = 200): void {
    this.shake = { x: 0, y: 0, strength, ttlMs: durationMs };
  }

  triggerLineFlash(rows: number[]): void {
    this.flashRows = rows;
    this.flashTtlMs = 160;
  }

  triggerTopout(): void {
    this.topoutTtlMs = this.topoutDur;
  }

  showControlsHint(): void {
    this.controlsHintTtlMs = this.controlsHintLife;
  }

  /** True when the viewport is too narrow to host the sidebars next to the board. */
  isCompact = false;

  layout(viewportWidth: number, viewportHeight: number): { cellPx: number; boardX: number; boardY: number; boardW: number; boardH: number; sidebarW: number; } {
    // Target board area aspect = 10 : 20. Leave margin and sidebars.
    const margin = 16;
    // Reserve a sidebar zone that scales with viewport; collapse on narrow viewports.
    const sideboardW = viewportWidth >= 1000 ? 220 : viewportWidth >= 760 ? 160 : 0;
    this.isCompact = sideboardW === 0;
    const availW = viewportWidth - margin * 2 - sideboardW * 2;
    const availH = viewportHeight - margin * 2 - (this.isCompact ? 96 : 0); // leave space for compact top/bottom strips
    const cellByW = Math.floor(availW / BOARD_WIDTH);
    const cellByH = Math.floor(availH / BOARD_VISIBLE_HEIGHT);
    const cellPx = Math.max(14, Math.min(cellByW, cellByH));
    const boardW = cellPx * BOARD_WIDTH;
    const boardH = cellPx * BOARD_VISIBLE_HEIGHT;
    const boardX = Math.floor((viewportWidth - boardW) / 2);
    const boardY = Math.floor((viewportHeight - boardH) / 2);
    return { cellPx, boardX, boardY, boardW, boardH, sidebarW: sideboardW };
  }

  resize(w: number, h: number, dpr: number): void {
    const cw = Math.floor(w * dpr);
    const ch = Math.floor(h * dpr);
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
      this.canvas.style.width = `${w}px`;
      this.canvas.style.height = `${h}px`;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Top-level render: paint background + board + sidebars + active piece + ghost + particles. */
  render(
    engine: Engine,
    particles: Particles,
    accolades: Accolades | null,
    viewport: { w: number; h: number },
    extras: {
      modeName: string;
      modeMetric: { label: string; value: string };
      timeMs: number;
      previewCount: number;
      activeAlphaBoost?: number;
      countdown?: string | null;
      paused?: boolean;
      finished?: boolean;
      gameOver?: boolean;
      glowIntensity?: number;
      lastScoreDelta?: { value: number; key: number } | null;
    },
  ): void {
    // Update animation timers (caller passes dt elsewhere via update()).
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = this.palette.bg;
    ctx.fillRect(0, 0, viewport.w, viewport.h);
    this.drawBackdrop(viewport);
    const layout = this.layout(viewport.w, viewport.h);
    // Apply shake.
    if (this.shake.ttlMs > 0) {
      const t = this.shake.ttlMs / 200;
      this.shake.x = (Math.random() - 0.5) * this.shake.strength * t;
      this.shake.y = (Math.random() - 0.5) * this.shake.strength * t;
      ctx.translate(this.shake.x, this.shake.y);
    }
    this.drawBoardFrame(layout);
    this.drawGrid(engine, layout);
    this.drawActivePiece(engine, layout);
    this.drawGhost(engine, layout);
    // Particles use the same coordinate space as the board.
    ctx.save();
    ctx.translate(layout.boardX, layout.boardY);
    particles.draw(ctx);
    if (accolades) accolades.draw(ctx);
    ctx.restore();
    // Sidebars (full layout) or compact strips above/below the board
    if (this.isCompact) {
      this.drawCompactHud(engine, layout, extras);
    } else {
      this.drawHold(engine, layout);
      this.drawNext(engine, layout, extras.previewCount);
      this.drawHud(engine, layout, viewport, extras);
    }
    // Center overlays (countdown / paused / game-over)
    if (extras.countdown) this.drawCenterText(layout, extras.countdown, this.palette.accent, 96);
    if (extras.paused) this.drawPauseOverlay(layout, engine);
    if (extras.finished) this.drawCenterText(layout, 'COMPLETE', this.palette.accent2, 48);
    if (extras.gameOver) this.drawTopoutOverlay(layout);
    // Controls hint at bottom of screen on game start
    if (this.controlsHintTtlMs > 0 && !extras.paused && !extras.gameOver && !extras.countdown) {
      this.drawControlsHint(layout, viewport);
    }
    ctx.restore();
  }

  /** Update internal animation/shake timers. */
  update(dtMs: number): void {
    if (this.shake.ttlMs > 0) this.shake.ttlMs = Math.max(0, this.shake.ttlMs - dtMs);
    if (this.flashTtlMs > 0) this.flashTtlMs = Math.max(0, this.flashTtlMs - dtMs);
    if (this.topoutTtlMs > 0) this.topoutTtlMs = Math.max(0, this.topoutTtlMs - dtMs);
    if (this.controlsHintTtlMs > 0) this.controlsHintTtlMs = Math.max(0, this.controlsHintTtlMs - dtMs);
  }

  // ---------- internals ----------

  private drawBackdrop(viewport: { w: number; h: number }): void {
    const { ctx } = this;
    // Vertical gradient
    const grad = ctx.createLinearGradient(0, 0, 0, viewport.h);
    grad.addColorStop(0, this.palette.bg2);
    grad.addColorStop(1, this.palette.bg);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, viewport.w, viewport.h);
    // Subtle radial accent
    const cx = viewport.w / 2, cy = viewport.h / 2;
    const r = Math.max(viewport.w, viewport.h) / 1.5;
    const rg = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    rg.addColorStop(0, this.hexAlpha(this.palette.accent, 0.05));
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, viewport.w, viewport.h);
  }

  private drawBoardFrame(layout: { cellPx: number; boardX: number; boardY: number; boardW: number; boardH: number; }): void {
    const { ctx } = this;
    // Fill starts EXACTLY at boardY so the visible top edge aligns with hold/queue panel tops.
    // The glow comes from shadowBlur which radiates outward without changing the fill bounds.
    ctx.save();
    ctx.shadowColor = this.palette.accent;
    ctx.shadowBlur = 22;
    ctx.fillStyle = this.hexAlpha(this.palette.bg2, 0.72);
    this.roundedRect(layout.boardX, layout.boardY, layout.boardW, layout.boardH, 4);
    ctx.fill();
    ctx.restore();
    // Thin inner border — soft, single-pixel, low-contrast.
    ctx.strokeStyle = this.hexAlpha(this.palette.accent, 0.35);
    ctx.lineWidth = 1;
    this.roundedRect(layout.boardX + 0.5, layout.boardY + 0.5, layout.boardW - 1, layout.boardH - 1, 3);
    ctx.stroke();
  }

  private drawGrid(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; },
  ): void {
    const { ctx } = this;
    const { cellPx, boardX, boardY } = layout;
    if (this.options.showGrid) {
      ctx.strokeStyle = this.palette.grid;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 1; x < BOARD_WIDTH; x++) {
        const px = boardX + x * cellPx + 0.5;
        ctx.moveTo(px, boardY);
        ctx.lineTo(px, boardY + BOARD_VISIBLE_HEIGHT * cellPx);
      }
      for (let y = 1; y < BOARD_VISIBLE_HEIGHT; y++) {
        const py = boardY + y * cellPx + 0.5;
        ctx.moveTo(boardX, py);
        ctx.lineTo(boardX + BOARD_WIDTH * cellPx, py);
      }
      ctx.stroke();
    }
    // Stamped blocks
    for (let y = 0; y < BOARD_VISIBLE_HEIGHT; y++) {
      const flashing = this.flashTtlMs > 0 && this.flashRows.includes(y + BOARD_BUFFER_HEIGHT);
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const c = engine.board.at(x, y + BOARD_BUFFER_HEIGHT);
        if (c === null) continue;
        const px = boardX + x * cellPx;
        const py = boardY + y * cellPx;
        this.drawCell(px, py, cellPx, this.colorOf(c), flashing);
      }
    }
  }

  private drawActivePiece(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; },
  ): void {
    if (!engine.piece) return;
    const { cellPx, boardX, boardY } = layout;
    const color = this.colorOf(engine.piece.type);
    const shape = shapeOf(engine.piece.type, engine.piece.rotation);
    // Lock-delay pulse: when the piece is grounded, brighten it as it approaches lock.
    const grounded = engine.board.collides({ ...engine.piece, y: engine.piece.y + 1 });
    let pulseAmt = 0;
    if (grounded) {
      // Sine pulse keyed off engine.touchedMs.
      const t = (engine.touchedMs ?? 0) / 500; // lock delay
      pulseAmt = 0.5 + 0.5 * Math.sin(t * Math.PI * 3); // oscillate
    }
    for (const [dx, dy] of shape) {
      const x = engine.piece.x + dx;
      const y = engine.piece.y + dy - BOARD_BUFFER_HEIGHT;
      if (y < 0) continue;
      this.drawCell(boardX + x * cellPx, boardY + y * cellPx, cellPx, color, false, true, pulseAmt);
    }
  }

  private drawGhost(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; },
  ): void {
    if (!engine.piece || !this.options.showGhost) return;
    const distance = engine.board.dropDistance(engine.piece);
    if (distance <= 0) return;
    const { cellPx, boardX, boardY } = layout;
    const shape = shapeOf(engine.piece.type, engine.piece.rotation);
    const color = this.colorOf(engine.piece.type);
    for (const [dx, dy] of shape) {
      const x = engine.piece.x + dx;
      const y = engine.piece.y + dy + distance - BOARD_BUFFER_HEIGHT;
      if (y < 0) continue;
      const px = boardX + x * cellPx, py = boardY + y * cellPx;
      this.ctx.save();
      this.ctx.globalAlpha = this.options.ghostAlpha;
      this.ctx.fillStyle = color;
      this.ctx.fillRect(px + 1, py + 1, cellPx - 2, cellPx - 2);
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 1.5;
      this.ctx.globalAlpha = Math.min(1, this.options.ghostAlpha * 4);
      this.ctx.strokeRect(px + 1, py + 1, cellPx - 2, cellPx - 2);
      this.ctx.restore();
    }
  }

  private drawCell(px: number, py: number, size: number, color: string, flashing: boolean, glow = false, pulse = 0): void {
    const { ctx } = this;
    if (flashing) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(px, py, size, size);
      return;
    }
    ctx.save();
    if (glow) {
      ctx.shadowColor = color;
      ctx.shadowBlur = 10 + pulse * 14;
    }
    if (this.options.rgbCellStyle === 'flat') {
      ctx.fillStyle = color;
      ctx.fillRect(px, py, size - 1, size - 1);
    } else if (this.options.rgbCellStyle === 'jewel') {
      const inset = 1;
      ctx.fillStyle = color;
      ctx.fillRect(px + inset, py + inset, size - inset * 2, size - inset * 2);
      // top-left highlight
      ctx.fillStyle = this.lighten(color, 0.35);
      ctx.fillRect(px + inset, py + inset, size - inset * 2, Math.max(1, Math.floor(size * 0.15)));
      ctx.fillRect(px + inset, py + inset, Math.max(1, Math.floor(size * 0.15)), size - inset * 2);
      // bottom-right shadow
      ctx.fillStyle = this.darken(color, 0.45);
      ctx.fillRect(px + inset, py + size - inset - Math.max(1, Math.floor(size * 0.15)), size - inset * 2, Math.max(1, Math.floor(size * 0.15)));
      ctx.fillRect(px + size - inset - Math.max(1, Math.floor(size * 0.15)), py + inset, Math.max(1, Math.floor(size * 0.15)), size - inset * 2);
      // center glint
      const innerSz = Math.max(2, Math.floor(size * 0.25));
      ctx.fillStyle = this.lighten(color, 0.55);
      ctx.globalAlpha = 0.6;
      ctx.fillRect(px + Math.floor(size * 0.3), py + Math.floor(size * 0.3), innerSz, Math.max(1, Math.floor(size * 0.1)));
    } else if (this.options.rgbCellStyle === 'beveled') {
      ctx.fillStyle = this.darken(color, 0.2);
      ctx.fillRect(px, py, size - 1, size - 1);
      ctx.fillStyle = color;
      ctx.fillRect(px + 2, py + 2, size - 5, size - 5);
    } else if (this.options.rgbCellStyle === 'outline') {
      // Outline-only — minimalist, lets the bloom carry the color.
      ctx.fillStyle = this.hexAlpha(color, 0.18);
      ctx.fillRect(px + 1, py + 1, size - 2, size - 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 2, py + 2, size - 4, size - 4);
    } else if (this.options.rgbCellStyle === 'glass') {
      // Glass / glossy: soft fill, gradient highlight, inner border.
      const grad = ctx.createLinearGradient(px, py, px, py + size);
      grad.addColorStop(0, this.lighten(color, 0.45));
      grad.addColorStop(0.5, color);
      grad.addColorStop(1, this.darken(color, 0.35));
      ctx.fillStyle = grad;
      ctx.fillRect(px + 1, py + 1, size - 2, size - 2);
      // top highlight
      ctx.fillStyle = `rgba(255,255,255,0.30)`;
      ctx.fillRect(px + 2, py + 2, size - 4, Math.max(2, Math.floor(size * 0.18)));
      // border
      ctx.strokeStyle = this.darken(color, 0.55);
      ctx.lineWidth = 1;
      ctx.strokeRect(px + 0.5, py + 0.5, size - 1, size - 1);
    } else if (this.options.rgbCellStyle === 'neon') {
      // Neon: hollow with strong glow and a bright inner bar.
      ctx.shadowColor = color;
      ctx.shadowBlur = 14 + pulse * 12;
      ctx.fillStyle = this.hexAlpha(color, 0.10);
      ctx.fillRect(px + 1, py + 1, size - 2, size - 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(px + 2, py + 2, size - 4, size - 4);
      // inner highlight
      ctx.fillStyle = this.lighten(color, 0.5);
      ctx.fillRect(px + Math.floor(size * 0.3), py + Math.floor(size * 0.3), Math.floor(size * 0.4), Math.max(1, Math.floor(size * 0.08)));
    } else if (this.options.rgbCellStyle === 'pixel') {
      // Pixel art-y: solid fill with single dark inner pixel border.
      ctx.fillStyle = color;
      ctx.fillRect(px, py, size, size);
      ctx.fillStyle = this.darken(color, 0.55);
      ctx.fillRect(px, py, size, 1);
      ctx.fillRect(px, py, 1, size);
      ctx.fillStyle = this.lighten(color, 0.35);
      ctx.fillRect(px + 1, py + 1, size - 2, 1);
      ctx.fillRect(px + 1, py + 1, 1, size - 2);
    } else {
      ctx.fillStyle = color;
      ctx.fillRect(px, py, size - 1, size - 1);
    }
    ctx.restore();
  }

  private drawHold(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; boardW: number; },
  ): void {
    const { ctx } = this;
    // Compact: 4.5 cells wide × 3 cells tall.
    const w = layout.cellPx * 4.5;
    const h = layout.cellPx * 3;
    const x = layout.boardX - w - layout.cellPx * 0.7;
    const y = layout.boardY;
    // Filled background to clearly read as a separate panel from the playfield.
    ctx.save();
    ctx.fillStyle = this.hexAlpha(this.palette.bg2, 0.85);
    this.roundedRect(x, y, w, h, 4);
    ctx.fill();
    ctx.strokeStyle = this.hexAlpha(this.palette.accent2, 0.55);
    ctx.lineWidth = 1;
    this.roundedRect(x, y, w, h, 4);
    ctx.stroke();
    ctx.restore();
    // Label above the box (outside the panel so it doesn't overlap the piece).
    ctx.fillStyle = this.palette.accent2;
    ctx.font = `700 12px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'bottom';
    ctx.textAlign = 'left';
    ctx.fillText('HOLD', x + 2, y - 4);
    if (engine.holdPiece) {
      this.drawPieceMini(engine.holdPiece, x + 4, y + 4, w - 8, h - 8, engine.holdLocked ? 0.35 : 1);
    }
  }

  private drawNext(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; boardW: number; boardH: number; },
    count: number,
  ): void {
    const { ctx } = this;
    // Each slot: 4.5 cells wide × 3 cells tall, stacked vertically.
    // First slot is taller for emphasis.
    const slotW = layout.cellPx * 4.5;
    const slotH = layout.cellPx * 3;
    const firstSlotH = layout.cellPx * 3.5;
    const gap = layout.cellPx * 0.25;
    const x = layout.boardX + layout.boardW + layout.cellPx * 0.7;
    const y = layout.boardY;
    // Label
    ctx.fillStyle = this.palette.accent;
    ctx.font = `700 12px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'bottom';
    ctx.textAlign = 'left';
    ctx.fillText('NEXT', x + 2, y - 4);
    const queue = engine.bag.peek(count);
    // Per-slot opacity curves — first slot full strength, then exponential fall-off.
    // Lower opacity for both the box chrome and the tetrimino itself, so the queue
    // visibly "fades into the background" as you look down it.
    // borderWidth is held at 1 across all slots to match the board / hold panel
    // strokes so the visual top edges line up to the pixel.
    const slotAlpha = (i: number): { piece: number; fill: number; border: number; borderWidth: number } => {
      if (i === 0) return { piece: 1.0, fill: 0.85, border: 0.75, borderWidth: 1 };
      const t = i;
      return {
        piece: Math.max(0.18, Math.pow(0.70, t)),
        fill: Math.max(0.18, 0.70 * Math.pow(0.78, t - 1)),
        border: Math.max(0.06, 0.40 * Math.pow(0.65, t - 1)),
        borderWidth: 1,
      };
    };
    let cy = y;
    for (let i = 0; i < count; i++) {
      const type = queue[i];
      if (!type) continue;
      const h = i === 0 ? firstSlotH : slotH;
      const a = slotAlpha(i);
      ctx.save();
      ctx.fillStyle = this.hexAlpha(this.palette.bg2, a.fill);
      this.roundedRect(x, cy, slotW, h, 4);
      ctx.fill();
      ctx.strokeStyle = this.hexAlpha(this.palette.accent, a.border);
      ctx.lineWidth = a.borderWidth;
      this.roundedRect(x, cy, slotW, h, 4);
      ctx.stroke();
      ctx.restore();
      this.drawPieceMini(type, x + 4, cy + 4, slotW - 8, h - 8, a.piece);
      cy += h + gap;
    }
  }

  private drawPieceMini(type: PieceType, x: number, y: number, w: number, h: number, alpha = 1): void {
    const shape = shapeOf(type, 0 as Rotation);
    let minX = 9, maxX = -1, minY = 9, maxY = -1;
    for (const [dx, dy] of shape) {
      if (dx < minX) minX = dx;
      if (dx > maxX) maxX = dx;
      if (dy < minY) minY = dy;
      if (dy > maxY) maxY = dy;
    }
    const wCells = maxX - minX + 1;
    const hCells = maxY - minY + 1;
    const cell = Math.floor(Math.min(w / 5, h / 3));
    const px = x + (w - wCells * cell) / 2 - minX * cell;
    const py = y + (h - hCells * cell) / 2 - minY * cell;
    this.ctx.save();
    this.ctx.globalAlpha = alpha;
    const color = this.colorOf(type);
    for (const [dx, dy] of shape) {
      this.drawCell(px + dx * cell, py + dy * cell, cell, color, false);
    }
    this.ctx.restore();
  }

  private drawHud(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; boardW: number; boardH: number; },
    viewport: { w: number; h: number },
    extras: { modeName: string; modeMetric: { label: string; value: string }; timeMs: number; },
  ): void {
    const { ctx } = this;
    const x = layout.boardX - layout.boardW * 0.6;
    const y = layout.boardY + layout.boardH * 0.45;
    const w = layout.boardW * 0.55;
    ctx.fillStyle = this.palette.textDim;
    ctx.font = `500 ${Math.floor(layout.cellPx * 0.42)}px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'top';
    const lh = Math.floor(layout.cellPx * 0.55);
    let cy = y;
    const draw = (label: string, value: string) => {
      ctx.fillStyle = this.palette.textDim;
      ctx.fillText(label, x, cy);
      ctx.fillStyle = this.palette.text;
      ctx.font = `700 ${Math.floor(layout.cellPx * 0.65)}px 'JetBrains Mono', ui-monospace, monospace`;
      ctx.fillText(value, x, cy + lh * 0.8);
      ctx.font = `500 ${Math.floor(layout.cellPx * 0.42)}px 'JetBrains Mono', ui-monospace, monospace`;
      cy += lh * 2.1;
    };
    draw('MODE', extras.modeName.toUpperCase());
    draw(extras.modeMetric.label.toUpperCase(), extras.modeMetric.value);
    draw('LEVEL', `${engine.stats.level}`);
    draw('LINES', `${engine.stats.lines}`);
    draw('PIECES', `${engine.stats.pieces}`);
    const pps = engine.stats.pieces / Math.max(1, engine.stats.elapsedMs / 1000);
    draw('PPS', pps.toFixed(2));
    // Combo / B2B chips
    const chipX = x;
    let chipY = cy;
    const chipH = Math.floor(layout.cellPx * 0.7);
    if (engine.stats.combo >= 1) {
      this.chip(chipX, chipY, `combo x${engine.stats.combo + 1}`, this.palette.accent2, chipH);
      chipY += chipH + 6;
    }
    if (engine.stats.backToBack >= 1) {
      this.chip(chipX, chipY, `B2B ${engine.stats.backToBack}`, this.palette.accent, chipH);
      chipY += chipH + 6;
    }
  }

  /** Compact HUD for narrow viewports: top strip = mode + stats, bottom strip = next + hold. */
  private drawCompactHud(
    engine: Engine,
    layout: { cellPx: number; boardX: number; boardY: number; boardW: number; boardH: number; sidebarW: number; },
    extras: { modeName: string; modeMetric: { label: string; value: string }; previewCount: number; },
  ): void {
    const { ctx } = this;
    // Top strip: mode, primary metric, level, lines, pieces
    const padTop = 14;
    const x = layout.boardX;
    const y = padTop;
    ctx.save();
    ctx.font = `600 11px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.fillStyle = this.palette.textDim;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    const stats: [string, string][] = [
      ['MODE', extras.modeName.toUpperCase()],
      [extras.modeMetric.label.toUpperCase(), extras.modeMetric.value],
      ['LVL', `${engine.stats.level}`],
      ['LINES', `${engine.stats.lines}`],
      ['PCS', `${engine.stats.pieces}`],
    ];
    const w = layout.boardW;
    const colW = w / stats.length;
    stats.forEach(([label, value], i) => {
      const cx = x + colW * (i + 0.5);
      ctx.textAlign = 'center';
      ctx.fillStyle = this.palette.textDim;
      ctx.font = `500 10px 'JetBrains Mono', ui-monospace, monospace`;
      ctx.fillText(label, cx, y);
      ctx.fillStyle = this.palette.text;
      ctx.font = `700 14px 'JetBrains Mono', ui-monospace, monospace`;
      ctx.fillText(value, cx, y + 14);
    });
    // Bottom strip: HOLD on left, next 3 pieces on right
    const stripH = 44;
    const bx = layout.boardX;
    const by = layout.boardY + layout.boardH + 12;
    const holdW = stripH * 1.4;
    // hold box
    ctx.strokeStyle = this.palette.accent2;
    ctx.lineWidth = 1.2;
    this.roundedRect(bx, by, holdW, stripH, 4);
    ctx.stroke();
    if (engine.holdPiece) {
      this.drawPieceMini(engine.holdPiece, bx, by + 2, holdW, stripH - 4, engine.holdLocked ? 0.4 : 1);
    }
    ctx.fillStyle = this.palette.textDim;
    ctx.font = `500 9px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textAlign = 'left';
    ctx.fillText('HOLD', bx + 4, by + 4);
    // next pieces (3)
    const nextX = bx + holdW + 8;
    const nextW = layout.boardW - holdW - 8;
    const queue = engine.bag.peek(3);
    const slotW = nextW / 3;
    ctx.strokeStyle = this.palette.accent;
    this.roundedRect(nextX, by, nextW, stripH, 4);
    ctx.stroke();
    ctx.fillStyle = this.palette.textDim;
    ctx.fillText('NEXT', nextX + 4, by + 4);
    queue.forEach((t, i) => {
      this.drawPieceMini(t, nextX + slotW * i, by + 2, slotW, stripH - 4, i === 0 ? 1 : 0.7);
    });
    ctx.restore();
  }

  private chip(x: number, y: number, text: string, color: string, h: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.font = `700 ${Math.floor(h * 0.52)}px 'JetBrains Mono', ui-monospace, monospace`;
    const padX = 8;
    const w = ctx.measureText(text).width + padX * 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 10;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    this.roundedRect(x, y, w, h, h / 2);
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, x + padX, y + h / 2);
    ctx.restore();
  }

  private drawCenterText(
    layout: { boardX: number; boardY: number; boardW: number; boardH: number; cellPx: number },
    text: string,
    color: string,
    size: number,
    sub?: string,
  ): void {
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(layout.boardX, layout.boardY, layout.boardW, layout.boardH);
    ctx.fillStyle = color;
    ctx.shadowColor = color;
    ctx.shadowBlur = 24;
    ctx.font = `800 ${size}px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, layout.boardX + layout.boardW / 2, layout.boardY + layout.boardH / 2);
    if (sub) {
      ctx.font = `500 ${Math.floor(size * 0.32)}px 'JetBrains Mono', ui-monospace, monospace`;
      ctx.shadowBlur = 8;
      ctx.fillStyle = this.palette.textDim;
      ctx.fillText(sub, layout.boardX + layout.boardW / 2, layout.boardY + layout.boardH / 2 + size);
    }
    ctx.restore();
  }

  private drawPauseOverlay(
    layout: { boardX: number; boardY: number; boardW: number; boardH: number; cellPx: number },
    _engine: Engine,
  ): void {
    // Just a dim layer — the DOM pause panel handles the chrome / buttons.
    const { ctx } = this;
    ctx.save();
    ctx.fillStyle = 'rgba(7, 4, 15, 0.6)';
    ctx.fillRect(layout.boardX, layout.boardY, layout.boardW, layout.boardH);
    ctx.restore();
  }

  private drawTopoutOverlay(
    layout: { boardX: number; boardY: number; boardW: number; boardH: number; cellPx: number },
  ): void {
    const { ctx } = this;
    const t = 1 - this.topoutTtlMs / this.topoutDur;
    ctx.save();
    // Red dim grows over time.
    ctx.fillStyle = `rgba(80, 8, 16, ${0.45 + 0.4 * t})`;
    ctx.fillRect(layout.boardX, layout.boardY, layout.boardW, layout.boardH);
    // Title with scaled pulse.
    const cx = layout.boardX + layout.boardW / 2;
    const cy = layout.boardY + layout.boardH / 2;
    ctx.fillStyle = this.palette.danger;
    ctx.shadowColor = this.palette.danger;
    ctx.shadowBlur = 28;
    const size = 56 + 8 * Math.sin(t * Math.PI);
    ctx.font = `800 ${size}px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('TOP OUT', cx, cy);
    ctx.restore();
  }

  controlsHintText = 'ARROWS MOVE    DOWN SOFTDROP    UP/Z ROTATE    SPACE HARDDROP    C HOLD    ESC PAUSE';

  setControlsHintText(text: string): void { this.controlsHintText = text; }

  private drawControlsHint(
    _layout: { boardX: number; boardY: number; boardW: number; boardH: number; cellPx: number },
    viewport: { w: number; h: number },
  ): void {
    const { ctx } = this;
    const t = this.controlsHintTtlMs / this.controlsHintLife;
    // Fade out in the last 25% of life.
    const alpha = t > 0.25 ? 0.85 : Math.max(0, t / 0.25 * 0.85);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.font = `600 11px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    const tw = ctx.measureText(this.controlsHintText).width;
    const cx = viewport.w / 2;
    const by = viewport.h - 24;
    ctx.fillRect(cx - tw / 2 - 16, by - 24, tw + 32, 32);
    ctx.fillStyle = this.palette.text;
    ctx.shadowColor = this.palette.accent2;
    ctx.shadowBlur = 6;
    ctx.fillText(this.controlsHintText, cx, by);
    ctx.restore();
  }

  // ---------- helpers ----------

  private colorOf(c: Cell): string {
    if (c === null) return this.palette.grid;
    if (c === 'G') return this.palette.garbage;
    return this.palette.pieces[c];
  }

  private roundedRect(x: number, y: number, w: number, h: number, r: number): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  private lighten(hex: string, amount: number): string {
    const { r, g, b } = hexToRgb(hex);
    return `rgb(${Math.min(255, r + (255 - r) * amount)},${Math.min(255, g + (255 - g) * amount)},${Math.min(255, b + (255 - b) * amount)})`;
  }
  private darken(hex: string, amount: number): string {
    const { r, g, b } = hexToRgb(hex);
    return `rgb(${Math.max(0, r * (1 - amount))},${Math.max(0, g * (1 - amount))},${Math.max(0, b * (1 - amount))})`;
  }
  private hexAlpha(hex: string, a: number): string {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r},${g},${b},${a})`;
  }
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return { r: 255, g: 255, b: 255 };
  const v = parseInt(m[1]!, 16);
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
}
